import React, { Component, Fragment } from 'react';
import injectSheet from 'react-jss';

import {
  BULK_COPY_LIMIT,
  BULK_MOVE_LIMIT,
} from '~/common/constants/max_lengths';
import FolderPanel from '~/components/Shared/FolderPanel/FolderPanel';
import { Button } from '~/eds/src/components/Button/Button';
import { withIsSelectedHash } from '~/hocs/withIsSelectedHash';
import { withTreeNodeKeyDownHandler } from '~/hocs/withTreeNodeKeyDownHandler';
import { injectIntl } from 'react-intl';
import Alert from '~/ui/components/Alert/Alert';
import { TreeNode } from '~/utils/TreeNode';

import { ERROR } from '../../../types/toast.types';
import EcFolderTreeItem from '../../Shared/EcFolderTreeItem/EcFolderTreeItem';
import EcModalCard from '../../Shared/EcModalCard/EcModalCard';
import { showToast } from '../../Shared/EcToast/showToast';
import LoadingSpinner from '../../Shared/Icons/LoadingSpinner';
import { getFolderTree } from '../../UploadPage/UploadPage.data';
import styles from './FolderTreeModal.styles';

const FolderTreeItem = withTreeNodeKeyDownHandler(EcFolderTreeItem);

class FolderTreeModal extends Component {
  state = {
    loading: true,
    folderTree: null,
    uploadLocation: null,
    uploadLocations: [],
    documentCount: null,
    overServerLimit: false,
  };

  async componentDidMount() {
    const {
      fetchDocumentsCount,
      uploadLocation,
      overLimit,
      bulkAction,
      enableSearch,
      intl,
    } = this.props;
    const defaultFetchDocuments = () => Promise.resolve({});
    const fetchCount =
      fetchDocumentsCount && !overLimit
        ? fetchDocumentsCount
        : defaultFetchDocuments;
    const limitCountPerActionType =
      bulkAction === 'Copy' ? BULK_COPY_LIMIT : BULK_MOVE_LIMIT;

    try {
      const [folderTree, { document_count }] = await Promise.all([
        getFolderTree(),
        fetchCount(),
      ]);
      const newUploadLocation = uploadLocation || folderTree;
      this.setState({
        folderTree: new TreeNode(folderTree),
        uploadLocation: !enableSearch ? newUploadLocation : null,
        loading: false,
        overServerLimit: document_count > limitCountPerActionType,
      });
    } catch {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'FolderTreeModal.errorLoadingFolders',
          defaultMessage: 'An error occurred while fetching a folder tree.',
          description: 'Error message when folders fail to load',
        }),
      );
    }
  }

  handleUploadSelect = (folder) => {
    if (folder.editable) {
      this.props.setIsSelected(this.state.uploadLocation.id, false);
      this.props.setIsSelected(folder.id, true);
      this.setState({ uploadLocation: folder.treeNode });
    }
  };

  handleUploadLocationMultiSelect = (location, checked) => {
    const { uploadLocations } = this.state;

    if (checked) {
      this.setState({
        uploadLocations: [...uploadLocations, location.treeNode],
      });
    } else {
      let newFolders = uploadLocations.filter((f) => f.id !== location.id);
      this.setState({ uploadLocations: newFolders });
    }
  };

  handleUploadMultiSelect = (folder) => {
    if (!folder.path?.length) return;

    if (!this.props.folderIdsSelected?.includes(folder.id)) {
      const isSelected = this.props.getIsSelected(folder.id);

      this.handleUploadLocationMultiSelect(folder, !isSelected);
      this.props.setIsSelected(folder.id, !isSelected);
    }
  };

  handleOnChooseLocationClick = () => {
    const { handleUploadLocationChange, hideModal } = this.props;
    const { uploadLocation } = this.state;

    handleUploadLocationChange(uploadLocation);
    hideModal();
  };

  handleChooseMultipleLocationsClick = () => {
    const { handleChooseMultipleLocationsClick, hideModal } = this.props;
    const { uploadLocations } = this.state;

    handleChooseMultipleLocationsClick(uploadLocations);
    hideModal();
  };

  handleSelectFolder = (folder) => {
    if (this.props.isMultiSelect) {
      this.handleUploadMultiSelect(folder);
    } else {
      this.handleUploadSelect(folder);
    }
  };

  handleSelectFolderSearch = (folder) => {
    this.setState({ uploadLocation: folder });
  };

  renderLimitWarning() {
    const { bulkAction, overLimit, intl } = this.props;
    const { overServerLimit } = this.state;
    const limitCountPerActionType =
      bulkAction === 'Copy' ? BULK_COPY_LIMIT : BULK_MOVE_LIMIT;

    if (overLimit || overServerLimit) {
      return (
        <Alert enableIcon variant="warning">
          {bulkAction === 'Copy'
            ? intl.formatMessage(
                {
                  id: 'FolderTreeModal.copyLimitWarning',
                  defaultMessage:
                    'Only {limit} documents can be copied at a time. Please select fewer documents to complete this action.',
                  description:
                    'Warning message about document limit for copy operations. Example: limit = "100"',
                },
                {
                  limit: limitCountPerActionType,
                },
              )
            : intl.formatMessage(
                {
                  id: 'FolderTreeModal.moveLimitWarning',
                  defaultMessage:
                    'Only {limit} documents can be moved at a time. Please select fewer documents to complete this action.',
                  description:
                    'Warning message about document limit for move operations. Example: limit = "100"',
                },
                {
                  limit: limitCountPerActionType,
                },
              )}
        </Alert>
      );
    } else {
      return null;
    }
  }

  renderFolderTree() {
    const {
      classes,
      folderIdsSelected,
      isMultiSelect,
      getIsSelected,
      setIsSelected,
      disableSyncPaired,
      enableSearch,
    } = this.props;
    const { folderTree, uploadLocation, uploadLocations, loading } = this.state;

    return (
      <div className={classes.folderTree}>
        {loading ? (
          <div className={classes.loadingContainer}>
            <LoadingSpinner size="medium" />
          </div>
        ) : (
          <>
            {this.renderLimitWarning()}

            {enableSearch ? (
              <FolderPanel
                onSelect={this.handleSelectFolderSearch}
                folderPathId={uploadLocation?.id}
                selectedFolder={uploadLocation}
                disableSyncPaired={disableSyncPaired}
                shouldDisable={(folder) => {
                  return (
                    !folder.editable ||
                    !!(disableSyncPaired && folder.sync_paired)
                  );
                }}
              />
            ) : (
              <FolderTreeItem
                firstToOpenId={folderTree.id}
                folder={folderTree}
                folderIdsSelected={folderIdsSelected}
                getIsSelected={getIsSelected}
                isMultiSelect={isMultiSelect}
                onSelect={this.handleSelectFolder}
                setIsSelected={setIsSelected}
                uploadLocation={uploadLocation}
                uploadLocations={uploadLocations}
                disableSyncPaired={disableSyncPaired}
              />
            )}
          </>
        )}
      </div>
    );
  }

  render() {
    const {
      classes,
      title,
      confirmButtonText,
      hideModal,
      isMultiSelect,
      folderIdsSelected,
      overLimit,
      intl,
    } = this.props;
    const { uploadLocation, uploadLocations, overServerLimit } = this.state;

    const handleOnClick = isMultiSelect
      ? this.handleChooseMultipleLocationsClick
      : this.handleOnChooseLocationClick;

    return (
      <EcModalCard
        title={title}
        content={<>{this.renderFolderTree()}</>}
        footer={
          <>
            {isMultiSelect && (
              <Fragment>
                <div className={classes.modalHelpText}>
                  {intl.formatMessage(
                    {
                      id: 'FolderTreeModal.foldersSelected',
                      defaultMessage: '{count} folder(s) selected',
                      description:
                        'Count of selected folders. Example: count = 3',
                    },
                    {
                      count: folderIdsSelected.length + uploadLocations.length,
                    },
                  )}
                </div>
              </Fragment>
            )}

            <Button
              variant="tertiary"
              text={intl.formatMessage({
                id: 'FolderTreeModal.cancelButton',
                defaultMessage: 'Cancel',
                description: 'Cancel button text',
              })}
              onClick={hideModal}
            />
            <Button
              text={confirmButtonText}
              onClick={handleOnClick}
              disabled={!uploadLocation?.id || overLimit || overServerLimit}
              variant="primary"
            />
          </>
        }
        hideModal={hideModal}
      />
    );
  }
}

export default injectSheet(styles)(
  injectIntl(withIsSelectedHash(FolderTreeModal)),
);
